import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Lock, Sparkles } from "lucide-react";
import { useEntitlement } from "@/hooks/use-entitlement";
import { useUpgrade } from "@/components/billing/premium-lock";

/**
 * Single access rule for the subscription-gated Resources features: any active
 * paid plan unlocks, everything else falls back to the free limits. Reads the
 * existing entitlement source — no separate subscription state.
 */
export function usePaidAccess(): { paid: boolean; loading: boolean } {
  const { entitlement, loading } = useEntitlement();
  return { paid: entitlement.paid_active === true, loading };
}

/** How many chapters per subject a non-subscriber may open. */
export const FREE_CHAPTER_LIMIT = 3;

/** Full-screen premium wall, styled with the existing premium/locked language. */
export function FeatureLockScreen({
  title,
  description,
  reason,
}: {
  title: string;
  description: string;
  reason: string;
}) {
  const { openUpgrade } = useUpgrade();
  return (
    <main className="page-shell px-5 lg:px-8 pb-16 pt-10">
      <div className="rounded-3xl border border-white/10 bg-card/70 p-7 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-[image:var(--gradient-brand)] text-primary-foreground">
          <Lock className="size-6" />
        </span>
        <p className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Premium Feature
        </p>
        <h1 className="mt-1.5 text-xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-2 text-[13px] text-muted-foreground">{description}</p>
        <button
          type="button"
          onClick={() => openUpgrade(reason)}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[image:var(--gradient-brand)] px-5 py-3 text-[13px] font-bold text-primary-foreground shadow-[var(--shadow-glow)] ring-1 ring-inset ring-border transition-transform active:scale-95"
        >
          <Sparkles className="size-4" />
          Subscribe to unlock
        </button>
        <Link
          to="/pricing"
          className="mt-2.5 inline-flex w-full items-center justify-center rounded-2xl border border-white/10 px-5 py-3 text-[13px] font-semibold text-muted-foreground transition active:scale-95"
        >
          View Plans
        </Link>
      </div>
    </main>
  );
}

/** Renders children only for active subscribers; otherwise the premium wall. */
export function PaidOnly({
  title,
  description,
  reason,
  children,
}: {
  title: string;
  description: string;
  reason: string;
  children: ReactNode;
}) {
  const { paid, loading } = usePaidAccess();
  if (loading) {
    return (
      <main className="page-shell px-5 lg:px-8 pt-10">
        <div className="h-64 animate-pulse rounded-3xl border border-white/10 bg-card/60" />
      </main>
    );
  }
  if (!paid) return <FeatureLockScreen title={title} description={description} reason={reason} />;
  return <>{children}</>;
}
